import type { ToolStepKind } from './tool-steps';

import { mcpLabel, toolInputDetail, TOOL_STEP_LABEL } from './tool-steps';

import { parseMcpCodeModeToolCall } from '@/lib/mcp-servers/tool-names';
import { getNonEmptyString, getRecord } from '@/lib/utils/unknown';

/** OpenCode's append-only, start-ordered calls within an `execute` script. */
export type CodeModeCall = {
  /** Original metadata array position, retained even when malformed entries are skipped. */
  index: number;
  tool: string;
  status: 'running' | 'completed' | 'error';
  input?: Record<string, unknown>;
};

export function codeModeCalls(metadata: unknown): CodeModeCall[] {
  const calls = getRecord(metadata)?.toolCalls;
  if (!Array.isArray(calls)) {
    return [];
  }
  return calls.flatMap((value, index): CodeModeCall[] => {
    const record = getRecord(value);
    const tool = getNonEmptyString(record?.tool);
    const status = record?.status;
    if (!tool || (status !== 'running' && status !== 'completed' && status !== 'error')) {
      return [];
    }
    return [{ index, tool, status, input: getRecord(record?.input) ?? undefined }];
  });
}

/** Missing metadata and older progress snapshots must not erase recorded calls or their end. */
export function mergeCodeModeCalls(
  previous: readonly CodeModeCall[],
  incoming: readonly CodeModeCall[],
): CodeModeCall[] {
  const calls = new Map(previous.map((call) => [call.index, call]));
  for (const call of incoming) {
    const before = calls.get(call.index);
    calls.set(
      call.index,
      before?.tool === call.tool
        ? {
            ...call,
            input: call.input ?? before.input,
            status: call.status === 'running' ? before.status : call.status,
          }
        : call,
    );
  }
  return [...calls.values()].sort((left, right) => left.index - right.index);
}

export function codeModeCallStep(
  call: CodeModeCall,
  parent: { running?: boolean; interrupted?: boolean },
): {
  kind: ToolStepKind;
  label: string;
  detail: string;
  running: boolean;
  failed: boolean;
  interrupted: boolean;
} {
  const mcp = parseMcpCodeModeToolCall(call.tool);
  return {
    kind: mcp ? 'mcp' : call.tool === 'fetch' ? 'web' : 'script',
    label: mcp ? mcpLabel([mcp]) : call.tool === 'fetch' ? TOOL_STEP_LABEL.web : call.tool,
    detail: toolInputDetail(call.tool, call.input),
    running: parent.running === true && call.status === 'running',
    failed: call.status === 'error',
    interrupted: parent.interrupted === true && call.status === 'running',
  };
}
